import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Plus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import RichTextEditor from '../RichTextEditor';
import useShareTargets from '../../hooks/useShareTargets';
import { EMAIL_TEMPLATE_CATEGORIES } from '../../lib/emailTemplateCategories';
import { errorText } from '../../lib/apiErrors';
import { ShareTargets, VisibilitySelect } from './SharingPicker';
import { ItemGroup, ItemRow } from './SharedItemList';
import { splitByOwner } from '../../lib/sharedItems';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const EMPTY_FORM = {
    name: '', subject: '', body: '', category: 'general',
    visibility: 'private', shared_with: [], shared_with_groups: [],
};

/**
 * Email templates tab of Settings → Templates & Signatures (#955).
 *
 * Lists every template the caller can use — including ones made in the
 * Campaign Builder panel (`?include_campaign=1`), badged "Campaign" — in three
 * groups. Templates created here are sales-side (`for_campaigns: false`) so
 * they appear in every composer. Organisation-wide stays an admin action under
 * Admin → Email Templates, so this form offers Private and Shared only.
 */
export default function MyTemplatesTab() {
    const { user, getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    const [templates, setTemplates] = useState([]);
    const [adminTemplates, setAdminTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);   // null = closed, {} = new, template = edit
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [copying, setCopying] = useState(null);
    const { members, groups } = useShareTargets(editing !== null);

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/email-templates?include_campaign=1`, { headers: getAuthHeader() });
            setTemplates(res.data?.templates || []);
            setAdminTemplates(res.data?.admin_templates || []);
        } catch (err) {
            toast.error(errorText(err, 'Failed to load templates'));
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const refresh = async () => {
        await load();
        // Composers cache the template list; refresh it so changes show there too.
        queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    };

    const openNew = () => {
        setForm(EMPTY_FORM);
        setEditing({});
    };

    const openEdit = (t) => {
        setForm({
            name: t.name || '',
            subject: t.subject || '',
            body: t.body || '',
            category: t.category || 'general',
            // A personal "organization" template is a tier nobody else sees: treat as Private.
            visibility: t.visibility === 'shared' ? 'shared' : 'private',
            shared_with: t.shared_with || [],
            shared_with_groups: t.shared_with_groups || [],
        });
        setEditing(t);
    };

    const handleSave = async () => {
        if (!form.name.trim() || !form.subject.trim() || !form.body.trim()) {
            toast.error('Name, subject and body are required');
            return;
        }
        const shared = form.visibility === 'shared';
        const payload = {
            name: form.name.trim(),
            subject: form.subject.trim(),
            body: form.body,
            category: form.category,
            visibility: form.visibility,
            shared_with: shared ? form.shared_with : [],
            shared_with_groups: shared ? form.shared_with_groups : [],
        };
        setSaving(true);
        try {
            if (editing?.template_id) {
                await axios.put(`${API}/email-templates/${editing.template_id}`, payload, { headers: getAuthHeader() });
                toast.success('Template updated');
            } else {
                await axios.post(`${API}/email-templates`, { ...payload, for_campaigns: false }, { headers: getAuthHeader() });
                toast.success('Template created');
            }
            setEditing(null);
            await refresh();
        } catch (err) {
            toast.error(errorText(err, 'Failed to save template'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (t) => {
        if (!window.confirm(`Delete template "${t.name}"?`)) return;
        try {
            await axios.delete(`${API}/email-templates/${t.template_id}`, { headers: getAuthHeader() });
            toast.success('Template deleted');
            await refresh();
        } catch (err) {
            toast.error(errorText(err, 'Failed to delete template'));
        }
    };

    const handleCopy = async (t) => {
        setCopying(t.template_id);
        try {
            await axios.post(`${API}/email-templates/${t.template_id}/fork`, {}, { headers: getAuthHeader() });
            toast.success(`Copied "${t.name}" to your templates`);
            await refresh();
        } catch (err) {
            toast.error(errorText(err, 'Failed to copy template'));
        } finally {
            setCopying(null);
        }
    };

    // Org masters can carry their author's user_id; they belong under Organisation only.
    const { mine, shared } = splitByOwner(templates.filter(t => !t.is_admin_template), user?.user_id);
    const row = (isOwner) => (t) => (
        <ItemRow
            key={t.template_id}
            item={t}
            idKey="template_id"
            kind="template"
            isOwner={isOwner}
            subtitle={t.subject || '(no subject)'}
            extraBadges={t.for_campaigns ? (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-50 text-indigo-700">Campaign</span>
            ) : null}
            onEdit={openEdit}
            onDelete={handleDelete}
            onCopy={handleCopy}
            copying={copying}
        />
    );

    if (loading) return <p className="text-sm text-stone-400 py-6">Loading templates…</p>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <p className="text-sm text-stone-500">Templates you can use in every composer.</p>
                <Button onClick={openNew} className="bg-stone-900 hover:bg-stone-800" data-testid="template-new-btn">
                    <Plus className="w-4 h-4 mr-1" /> New template
                </Button>
            </div>
            <ItemGroup
                title="Mine" testId="templates-mine" items={mine}
                emptyText="You have no templates yet."
                renderRow={row(true)}
            />
            <ItemGroup
                title="Shared with me" testId="templates-shared" items={shared}
                emptyText="Nobody has shared a template with you yet."
                renderRow={row(false)}
            />
            <ItemGroup
                title="Organisation" testId="templates-org" items={adminTemplates}
                description="Managed by your admins. Make a copy to change one."
                emptyText="Your organisation has no shared templates."
                renderRow={row(false)}
            />

            <Dialog open={editing !== null} onOpenChange={open => { if (!open) setEditing(null); }}>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Mail className="w-5 h-5" />
                            {editing?.template_id ? 'Edit template' : 'New template'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label htmlFor="template-name">Name</Label>
                            <Input id="template-name" data-testid="template-name-input" className="mt-1"
                                value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                                placeholder="e.g. Day-1 follow-up" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="template-category">Category</Label>
                                <select id="template-category" value={form.category}
                                    onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                                    className="mt-1 w-full px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white">
                                    {EMAIL_TEMPLATE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                </select>
                            </div>
                            <VisibilitySelect id="template-visibility" value={form.visibility} labelClassName="block text-sm font-medium leading-none mb-2"
                                onChange={visibility => setForm(p => ({ ...p, visibility }))} />
                        </div>
                        <ShareTargets
                            visibility={form.visibility}
                            sharedWith={form.shared_with}
                            sharedWithGroups={form.shared_with_groups}
                            onChange={targets => setForm(p => ({ ...p, ...targets }))}
                            members={members}
                            groups={groups}
                            currentUserId={user?.user_id}
                            idPrefix="template-share"
                        />
                        <div>
                            <Label htmlFor="template-subject">Subject</Label>
                            <Input id="template-subject" data-testid="template-subject-input" className="mt-1"
                                value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                                placeholder="Email subject line" />
                        </div>
                        <div>
                            <Label>Body</Label>
                            <div className="mt-1" data-testid="template-body-editor">
                                <RichTextEditor
                                    content={form.body}
                                    onChange={html => setForm(p => ({ ...p, body: html }))}
                                    placeholder="Write your template…"
                                    minHeight="180px"
                                    showMergeVariables
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-stone-900 hover:bg-stone-800" data-testid="template-save-btn">
                            {saving ? 'Saving…' : editing?.template_id ? 'Update template' : 'Create template'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
